import React, { useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import InlineLoader from "./InlineLoader";
import { getBillingInvoice } from "services/billing";

import InvoiceStatusChip from "./InvoiceStatusChip";
import { TableComponent } from "./ui";
import { BsInvoice, formatDate, formatMoney, titleCase } from "./types";

const lineItemColumns = [
  { id: "description", label: "Description" },
  { id: "type", label: "Type", render: (v: string) => titleCase(v) },
  { id: "quantity", label: "Qty" },
  { id: "amountCents", label: "Amount", render: (v: number) => formatMoney(v) },
];

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-025 border rounded-lg p-075" style={{ minWidth: 150 }}>
      <p className="para-body-s-medium color-text-body-secondary">{label}</p>
      <p className="para-body-m-medium color-text-body-primary">{value}</p>
    </div>
  );
}

export default function InvoiceDetailModal({ invoiceId, onClose }: { invoiceId: string; onClose: () => void }) {
  const [invoice, setInvoice] = useState<BsInvoice | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const data = await getBillingInvoice(invoiceId);
        if (active) setInvoice(data);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [invoiceId]);

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <div className="flex justify-between items-center">
          <p className="heading-h4-semibold color-text-heading-primary">Invoice</p>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <InlineLoader />
        ) : !invoice ? (
          <p className="para-body-m-regular color-text-body-secondary">Invoice not found.</p>
        ) : (
          <div className="flex flex-col gap-1">
            <div className="flex flex-wrap gap-075">
              <Field label="Status" value={<InvoiceStatusChip status={invoice.status} metadata={invoice.metadata} />} />
              <Field label="Type" value={titleCase(invoice.type)} />
              <Field label="Total" value={formatMoney(invoice.totalAmountCents, invoice.currency)} />
              <Field label="Due" value={formatDate(invoice.dueDate)} />
              <Field label="Paid" value={formatDate(invoice.paidAt)} />
              <Field
                label="Billing period"
                value={`${formatDate(invoice.billingPeriodStart)} to ${formatDate(invoice.billingPeriodEnd)}`}
              />
              {invoice.voidedAt && <Field label="Voided" value={formatDate(invoice.voidedAt)} />}
              {invoice.metadata?.voidReason === "paused_cycle_skipped" && (
                <Field label="Reason" value="Skipped while paused" />
              )}
            </div>
            <div className="flex flex-col gap-05 mt-05">
              <p className="heading-h5-medium color-text-heading-primary">Line items</p>
              <TableComponent columns={lineItemColumns} data={invoice.lineItems ?? []} disableSort tableCellSmall />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
